import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { reportService } from '../../../features/reports/services/reportService'
import type { Report } from '../../../features/reports/services/reportService'
import { assemblePdfData } from '../../../features/reports/services/pdfDataService'
import { renderReportPdfBlob } from '../../../features/reports/services/pdfRenderService'
import { brandSnapshot } from '../../../features/reports/services/reportBrandService'
import { ImageFetchError } from '../../../features/reports/services/pdfImageService'
import {
  discardUnsavedPdf,
  keepUnsavedPdf,
} from '../../../features/reports/services/unsavedPdfStore'
import { validateReportReadiness } from '../../../features/reports/services/validationService'
import type { ValidationIssue } from '../../../features/reports/services/validationService'
import type { ReportDocumentProps } from '../../../features/reports/components/ReportDocument'
import { supabase } from '../../../services/supabase/client'
import type { Database, Json } from '../../../types/database.types'
import { isReportType } from '../../../features/reports/config/reportType'
import type { ReportType } from '../../../features/reports/config/reportType'
import { logout } from './authSlice'

// Pipeline stages for structured generation errors and progress UI (RPT-03)
export type GenerationStage = 'validation' | 'image-fetch' | 'render' | 'upload'

export interface GenerationFailure {
  stage: GenerationStage
  message: string
  retryable: boolean
  /** Present only for stage === 'validation' — drives the blocking checklist */
  validationIssues?: ValidationIssue[]
}

export interface GenerationProgress {
  stage: GenerationStage
  /** Populated during image-fetch */
  imagesDone?: number
  imagesTotal?: number
}

/** Parse a reports.generation_error column value written by this slice */
export function parseGenerationError(raw: string | null): GenerationFailure | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<GenerationFailure>
    if (typeof parsed.stage === 'string' && typeof parsed.message === 'string') {
      return {
        stage: parsed.stage,
        message: parsed.message,
        retryable: parsed.retryable !== false,
      }
    }
  } catch {
    // Legacy free-text value
  }
  return { stage: 'render', message: raw, retryable: true }
}

interface ReportsState {
  items: Report[]
  loading: boolean
  generating: boolean
  saving: boolean
  error: string | null
  generationError: string | null
  generationFailure: GenerationFailure | null
  generationProgress: GenerationProgress | null
  saveError: string | null
}

const initialState: ReportsState = {
  items: [],
  loading: false,
  generating: false,
  saving: false,
  error: null,
  generationError: null,
  generationFailure: null,
  generationProgress: null,
  saveError: null,
}

export const fetchReportsByJobId = createAsyncThunk(
  'reports/fetchByJobId',
  async (jobId: string) => {
    return await reportService.getReportsByJobId(jobId)
  }
)

/**
 * Largest PDF the `reports` bucket accepts: Supabase's free-plan upload
 * limit (50 MB). Checked before uploading so the user is told the real
 * reason, not a storage error.
 */
export const REPORT_UPLOAD_MAX_BYTES = 50_000_000

const megabytes = (bytes: number) => `${(bytes / 1_000_000).toFixed(1)} MB`

/**
 * Keyed by report id since 008: a full report and a findings report can share
 * a job and a version, so (jobId, version) no longer identifies a row.
 */
export const generateReport = createAsyncThunk<
  Report,
  { reportId: string },
  { rejectValue: GenerationFailure }
>('reports/generate', async ({ reportId }, { dispatch, rejectWithValue }) => {
  // Require an authenticated user — their ID is stored on the report record
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) {
    return rejectWithValue({
      stage: 'validation',
      message: 'Not authenticated',
      retryable: false,
    })
  }

  // Fetch the saved report record to get report_data (images, text, toggles)
  // and top-level summary/recommendations columns
  const { data: savedReport } = await supabase
    .from('reports')
    .select('id, job_id, version, report_type, report_data, summary, overall_recommendations')
    .eq('id', reportId)
    .maybeSingle()

  if (!savedReport) {
    return rejectWithValue({
      stage: 'validation',
      message: 'This report no longer exists',
      retryable: false,
    })
  }
  const jobId = savedReport.job_id
  const version = savedReport.version
  const reportType: ReportType = isReportType(savedReport.report_type)
    ? savedReport.report_type
    : 'full_report'
  const reportRowId = savedReport.id
  // A new attempt supersedes any PDF kept from a failed upload
  discardUnsavedPdf(reportRowId)

  // Persist the failure on the report row so it stays actionable across
  // sessions (RPT-03), then reject with the same structured payload
  const fail = async (failure: GenerationFailure) => {
    if (reportRowId) {
      await supabase
        .from('reports')
        .update({
          generation_error: JSON.stringify({
            stage: failure.stage,
            message: failure.message,
            retryable: failure.retryable,
          }),
        })
        .eq('id', reportRowId)
    }
    return rejectWithValue(failure)
  }

  // Mark generation start on the row (clears any previous failure)
  if (reportRowId) {
    await supabase
      .from('reports')
      .update({ generation_started_at: new Date().toISOString(), generation_error: null })
      .eq('id', reportRowId)
  }

  // ---------------------------------------------------------------------
  // Stage 1: validation — blocking completeness checklist (FR-020)
  // ---------------------------------------------------------------------
  dispatch(setGenerationProgress({ stage: 'validation' }))
  const savedReportData = savedReport.report_data as Record<string, unknown> | null | undefined
  try {
    const validation = await validateReportReadiness(
      jobId,
      reportType,
      (savedReportData ?? {}) as Record<string, boolean | undefined>
    )
    if (!validation.valid) {
      return await fail({
        stage: 'validation',
        message: `${validation.issues.length} item(s) need attention — tap to review and fix them`,
        retryable: false,
        validationIssues: validation.issues,
      })
    }
  } catch (err) {
    return await fail({
      stage: 'validation',
      message: err instanceof Error ? err.message : 'Validation checks could not be run',
      retryable: true,
    })
  }

  // ---------------------------------------------------------------------
  // Stage 2: image fetch — data assembly with embedded images (RPT-02)
  // ---------------------------------------------------------------------
  dispatch(setGenerationProgress({ stage: 'image-fetch' }))
  let reportData: ReportDocumentProps
  try {
    reportData = await assemblePdfData(
      jobId,
      version,
      savedReport.summary,
      savedReport.overall_recommendations,
      savedReportData,
      (done, total) =>
        dispatch(
          setGenerationProgress({ stage: 'image-fetch', imagesDone: done, imagesTotal: total })
        ),
      { variant: reportType }
    )
  } catch (err) {
    const message =
      err instanceof ImageFetchError
        ? `${err.message}. Check your connection and retry.`
        : err instanceof Error
          ? err.message
          : 'Failed to fetch report data'
    return await fail({ stage: 'image-fetch', message, retryable: true })
  }

  // ---------------------------------------------------------------------
  // Stage 3: render — single-pass PDF build, fully offline from here
  // ---------------------------------------------------------------------
  dispatch(setGenerationProgress({ stage: 'render' }))
  let blob: Blob
  try {
    blob = await renderReportPdfBlob(reportData)
  } catch (err) {
    return await fail({
      stage: 'render',
      message: err instanceof Error ? err.message : 'PDF rendering failed',
      retryable: true,
    })
  }

  // ---------------------------------------------------------------------
  // Stage 4: upload + record. If the PDF is over the upload limit or the
  // upload fails (e.g. offline), keep the finished PDF in memory so the
  // work isn't lost (unsavedPdfStore) and surface the error; the pages offer
  // a "Save PDF" button.
  // ---------------------------------------------------------------------
  dispatch(setGenerationProgress({ stage: 'upload' }))
  const filename = `report-${reportType}_v${version}.pdf`
  if (blob.size > REPORT_UPLOAD_MAX_BYTES) {
    keepUnsavedPdf(reportRowId, blob, filename)
    return await fail({
      stage: 'upload',
      message:
        `The PDF is ${megabytes(blob.size)}, over the ${megabytes(REPORT_UPLOAD_MAX_BYTES)} ` +
        'upload limit, so it was not stored online. Save it to this device instead.',
      retryable: false,
    })
  }
  let report: Report
  try {
    const { path: pdfPath, size: pdfSize } = await reportService.uploadPdf(
      blob,
      jobId,
      version,
      reportType
    )
    // Snapshot which brand/layout produced this PDF (003) — immutable record
    // for the version stamp and the "regenerate to apply vN" nudge — and,
    // for a full report, the section order it printed in (010 FR-012)
    const snapshot = {
      ...(reportData.brand ? { brand: brandSnapshot(reportData.brand) } : {}),
      ...(reportType === 'full_report' && reportData.sectionOrder
        ? { section_order: reportData.sectionOrder }
        : {}),
    }
    const metadata = Object.keys(snapshot).length > 0 ? snapshot : null
    report = await reportService.saveReportRecord(
      jobId,
      version,
      pdfPath,
      pdfSize,
      user.id,
      metadata,
      reportType
    )
  } catch (err) {
    keepUnsavedPdf(reportRowId, blob, filename)
    const message = err instanceof Error ? err.message : 'Upload failed'
    return await fail({
      stage: 'upload',
      message: `${message} (PDF ${megabytes(blob.size)}). Save it to this device, or retry to store it online.`,
      retryable: true,
    })
  }

  // Client notification moved to delivery (Phase 6): clients cannot see a
  // report until it is delivered, so notifying at generation was premature.

  return report
})

export const markReportDelivered = createAsyncThunk(
  'reports/markDelivered',
  async ({ reportId, method }: { reportId: string; method: 'email' | 'portal' | 'manual' }) => {
    return await reportService.markDelivered(reportId, method)
  }
)

export const deleteReport = createAsyncThunk('reports/delete', async (id: string) => {
  await reportService.deleteReport(id)
  return id
})

export const saveReportData = createAsyncThunk(
  'reports/saveData',
  async ({
    jobId,
    reportType,
    data,
    reportId,
  }: {
    jobId: string
    reportType: ReportType
    data: Record<string, unknown>
    reportId?: string
  }) => {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Determine the next version if creating a new record
    let version = 1
    if (!reportId) {
      const { data: existing } = await supabase
        .from('reports')
        .select('version')
        .eq('job_id', jobId)
        .eq('report_type', reportType)
        .order('version', { ascending: false })
        .limit(1)
      if (existing && existing.length > 0 && existing[0] !== undefined) {
        version = (existing[0].version ?? 0) + 1
      }
    }

    const upsertPayload: Database['public']['Tables']['reports']['Update'] = {
      job_id: jobId,
      report_type: reportType,
      report_data: data as Json,
      generated_by: user.id,
      status: 'draft',
    }

    // Map the shared narrative fields to top-level columns for PDF compatibility
    // (both variants carry an executive summary and recommendations)
    if (typeof data['executive_summary'] === 'string') {
      upsertPayload.summary = data['executive_summary']
    }
    if (typeof data['recommendations'] === 'string') {
      upsertPayload.overall_recommendations = data['recommendations']
    }

    if (reportId) {
      // Update existing record
      const { data: updated, error } = await supabase
        .from('reports')
        .update(upsertPayload)
        .eq('id', reportId)
        .select()
        .single()
      if (error) throw new Error(`Failed to save report: ${error.message}`)
      return updated
    } else {
      // Insert new record with version
      const insertPayload: Database['public']['Tables']['reports']['Insert'] = {
        ...upsertPayload,
        job_id: jobId,
        generated_by: user.id,
        version,
      }
      const { data: inserted, error } = await supabase
        .from('reports')
        .insert(insertPayload)
        .select()
        .single()
      if (error) throw new Error(`Failed to save report: ${error.message}`)
      return inserted
    }
  }
)

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReports: (state) => {
      state.items = []
      state.error = null
      state.generationError = null
      state.generationFailure = null
      state.generationProgress = null
    },
    clearError: (state) => {
      state.error = null
      state.generationError = null
      state.generationFailure = null
      state.saveError = null
    },
    setGenerationProgress: (state, action: PayloadAction<GenerationProgress>) => {
      state.generationProgress = action.payload
    },
    clearGenerationFailure: (state) => {
      state.generationFailure = null
      state.generationError = null
    },
  },
  extraReducers: (builder) => {
    // Fetch reports
    builder
      .addCase(fetchReportsByJobId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReportsByJobId.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchReportsByJobId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch reports'
      })

    // Generate report
    builder
      .addCase(generateReport.pending, (state) => {
        state.generating = true
        state.generationError = null
        state.generationFailure = null
        state.generationProgress = null
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.generating = false
        state.generationProgress = null
        if (action.payload) {
          // Replace existing version or prepend new report
          const index = state.items.findIndex((r) => r.id === action.payload?.id)
          if (index !== -1) {
            state.items[index] = action.payload
          } else {
            state.items.unshift(action.payload)
          }
        }
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.generating = false
        state.generationProgress = null
        const failure: GenerationFailure = action.payload ?? {
          stage: 'render',
          message: action.error.message ?? 'Failed to generate report',
          retryable: true,
        }
        state.generationFailure = failure
        state.generationError = failure.message
        // Mirror the persisted generation_error column onto the row we hold,
        // so the list shows the failed state without a refetch
        const { reportId } = action.meta.arg
        const failedItem = state.items.find((r) => r.id === reportId)
        if (failedItem) {
          failedItem.generation_error = JSON.stringify({
            stage: failure.stage,
            message: failure.message,
            retryable: failure.retryable,
          })
        }
      })

    // Mark delivered
    builder
      .addCase(markReportDelivered.fulfilled, (state, action) => {
        const index = state.items.findIndex((r) => r.id === action.payload.id)
        if (index !== -1) state.items[index] = action.payload
      })
      .addCase(markReportDelivered.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to record delivery'
      })

    // Delete report
    builder
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload)
      })
      .addCase(deleteReport.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to delete report'
      })

    // Save report data
    builder
      .addCase(saveReportData.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(saveReportData.fulfilled, (state, action) => {
        state.saving = false
        if (action.payload) {
          const index = state.items.findIndex((r) => r.id === action.payload?.id)
          if (index !== -1) {
            state.items[index] = action.payload
          } else {
            state.items.unshift(action.payload)
          }
        }
      })
      .addCase(saveReportData.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.error.message ?? 'Failed to save report'
      })

    // Clear on logout
    builder.addCase(logout.fulfilled, () => initialState)
  },
})

export const { clearReports, clearError, setGenerationProgress, clearGenerationFailure } =
  reportsSlice.actions
export const reportsReducer = reportsSlice.reducer
